import type { ReactNode } from "react";
import type {
  CampaignReportResponse,
  ComparisonReportResponse,
  HessReportResponse,
  LinearReportResponse,
  SocialCampaignReportResponse,
  StreamingReportResponse,
} from "../../types";
import type { UploadResultsData as UploadResultsHookData } from "../../hooks/useUploadResultsReport";
import type { UploadResultsMetadata } from "../../hooks/useUploadResultsReport";
import { AjinomotoGyozaLayout } from "../../report-layouts/ajinomoto-gyoza";
import {
  CompactLayout,
  OriginalLayout,
  SlideLayout,
} from "../../report-layouts/campaign";
import { ComparisonLayout } from "../../report-layouts/comparison";
import { HessLayout } from "../../report-layouts/hess";
import { LinearLayout } from "../../report-layouts/linear";
import { OffsiteLayout } from "../../report-layouts/offsite";
import { OnsiteAwarenessLayout } from "../../report-layouts/onsite-awareness";
import { SocialLayout } from "../../report-layouts/social";
import { StreamingLayout } from "../../report-layouts/streaming";
import { getReportLayoutKind } from "../../utils/reportFamilyAdapters";

type ResolvedData = UploadResultsHookData;

export type ReportLayoutResolverProps = {
  currentReportId: string | null;
  data: ResolvedData;
  reportMetadata?: UploadResultsMetadata;
  handleNewUpload: () => void;
  reportProduct: string | null;
  viewMode: "original" | "slide" | "compact";
};

export function resolveUploadReportLayout({
  currentReportId,
  data,
  reportMetadata,
  handleNewUpload,
  reportProduct,
  viewMode,
}: ReportLayoutResolverProps): ReactNode {
  const layoutKind = getReportLayoutKind(data, reportProduct, viewMode);

  if (layoutKind === "gyoza") {
    return (
      <AjinomotoGyozaLayout
        data={data as CampaignReportResponse}
        reportId={currentReportId || undefined}
      />
    );
  }

  if (layoutKind === "comparison") {
    return (
      <ComparisonLayout
        data={data as ComparisonReportResponse}
        onNewUpload={handleNewUpload}
      />
    );
  }

  if (layoutKind === "streaming") {
    return (
      <StreamingLayout
        data={data as StreamingReportResponse}
        clientId={reportMetadata?.client}
      />
    );
  }

  if (layoutKind === "linear") {
    return (
      <LinearLayout
        data={data as LinearReportResponse}
        clientId={reportMetadata?.client}
      />
    );
  }

  if (layoutKind === "social") {
    return (
      <SocialLayout
        data={data as SocialCampaignReportResponse}
        reportId={currentReportId || undefined}
        clientId={reportMetadata?.client}
      />
    );
  }

  if (layoutKind === "hess") {
    return (
      <HessLayout
        data={data as HessReportResponse}
        reportId={currentReportId || undefined}
      />
    );
  }

  if (layoutKind === "offsite") {
    return (
      <OffsiteLayout
        data={data as CampaignReportResponse}
        onNewUpload={handleNewUpload}
        reportId={currentReportId || undefined}
        clientId={reportMetadata?.client}
        reportRetailer={reportMetadata?.retailer}
      />
    );
  }

  if (layoutKind === "onsite") {
    return (
      <OnsiteAwarenessLayout
        data={data as CampaignReportResponse}
        onNewUpload={handleNewUpload}
        reportId={currentReportId || undefined}
        clientId={reportMetadata?.client}
      />
    );
  }

  if (layoutKind === "campaign-original") {
    return (
      <OriginalLayout
        data={data as CampaignReportResponse}
        onNewUpload={handleNewUpload}
        reportId={currentReportId || undefined}
        clientId={reportMetadata?.client}
      />
    );
  }

  if (layoutKind === "campaign-slide") {
    return (
      <SlideLayout
        data={data as CampaignReportResponse}
        onNewUpload={handleNewUpload}
      />
    );
  }

  return (
    <CompactLayout
      data={data as CampaignReportResponse}
      onNewUpload={handleNewUpload}
    />
  );
}
